import React, { useEffect, useState, useRef, useCallback } from 'react';
// hooks
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
// store
import { getMatches, clearBills } from '../features/bills/billSlice';
import { createSelector } from 'reselect';
import { setSession } from '../features/user/userSlice';
// components
import Dropdown from '../components/Structures/Dropdown';
import Table from '../components/Structures/Table';
import { Button } from '../components/ui/Button';
import { StickyBar } from '../components/ui';
import { IoSearch, IoPrintOutline, IoCloseCircle } from 'react-icons/io5';
import DatePicker from 'react-datepicker'; // You'll need to install this package
import 'react-datepicker/dist/react-datepicker.css'; // Import the styles
import tdot from '../assets/images/tdot.jpg';

// Define your memoized selectors if they are not already defined elsewhere
const selectBills = createSelector(
  (state) => state.bill.bills,
  (bills) => bills
);

const selectTracks = createSelector(
  (state) => state.user.tracks,
  (tracks) => tracks
);

function SearchPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const mostRecentQuery = localStorage.getItem('mostRecentQuery');

  const { session, sessions } = useSelector((state) => state.user);
  const bills = useSelector(selectBills);
  const tracks = useSelector(selectTracks);

  const [query, setQuery] = useState(mostRecentQuery || '');
  const [results, setResults] = useState([[]]);
  const [isLoading, setIsLoading] = useState(false);
  const [isShowingFluffedResults, setIsShowingFluffedResults] = useState(false);
  const [numResults, setNumResults] = useState('20');
  const [minPercentage, setMinPercentage] = useState(25);
  const [sinceDate, setSinceDate] = useState(null); // New state for since date

  const [onlyStrongMatches, setOnlyStrongMatches] = useState(true);

  const inputRef = useRef(null);

  const handleBillClick = (billId) => {
    navigate(`../bill/${billId}?session=${session}`);
  };

  const handlePrintResults = () => {
    // Create a new window or use an iframe to format the print content
    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.write(
      '<html><head><title>Print Search Results</title><style>body { font-family: Arial; } table { width: 100%; border-collapse: collapse; } th, td { padding: 8px; text-align: left; border-bottom: 1px solid #ddd; } th { background-color: #f2f2f2; } .evenRow { background-color: #f9f9f9; } .oddRow { background-color: #e6e6e6; }</style></head><body>'
    );

    // Header row
    printWindow.document.write('<table>');
    printWindow.document.write('<thead>');
    printWindow.document.write('<tr>');
    printWindow.document.write('<th>Number</th>');
    printWindow.document.write('<th>Author</th>');
    printWindow.document.write('<th>Title</th>');
    printWindow.document.write('</tr>');
    printWindow.document.write('</thead>');
    printWindow.document.write('<tbody>');

    // Assuming `results` is an array containing objects with properties `id`, `metadata.author`, and `metadata.title`
    results.forEach((result, index) => {
      const rowClass = index % 2 === 0 ? 'evenRow' : 'oddRow';
      printWindow.document.write(
        `<tr class="${rowClass}"><td>${result.id}</td><td>${result.metadata.author}</td><td>${result.metadata.title}</td></tr>`
      ); // Customize based on your result object structure
    });

    printWindow.document.write('</tbody>');
    printWindow.document.write('</table>');
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  useEffect(() => {
    const sortedBills = [...bills].sort((a, b) => b.score - a.score);

    let filteredBills = sortedBills;

    if (onlyStrongMatches) {
      const percentageThreshold = 0.25; // Set the threshold for strong matches
      filteredBills = sortedBills.filter((bill) => bill.score >= percentageThreshold);
    }

    // Determine if showing fluffed results
    const showingFluffedResults = filteredBills.length < 3 && sortedBills.length > 0;

    // Update state based on the above logic
    setIsShowingFluffedResults(showingFluffedResults);

    // Determine the final bills to show based on whether we're fluffing the results
    const finalToShow = showingFluffedResults ? sortedBills.slice(0, 5) : filteredBills;

    setResults(finalToShow); // Assuming you use this to render your components
  }, [bills, minPercentage, numResults, session, onlyStrongMatches]);

  useEffect(() => {
    // Clear the search results and query when the session changes
    setResults([]);
    setQuery('');
    localStorage.removeItem('mostRecentQuery');

    // Clear the bills from the Redux store
    dispatch(clearBills());
  }, [session]);

  const handleCheckboxChange = (e) => {
    setOnlyStrongMatches(e.target.checked);
  };

  const performSearch = useCallback(async () => {
    if (query.trim() === '') {
      return;
    }
    localStorage.setItem('mostRecentQuery', query);
    setIsLoading(true);
    try {
      // Format the date to YYYY-MM-DD format if sinceDate is set
      const formattedSinceDate = sinceDate
        ? `${sinceDate.getFullYear()}-${String(sinceDate.getMonth() + 1).padStart(2, '0')}-${String(sinceDate.getDate()).padStart(2, '0')}`
        : null;

      await dispatch(
        getMatches({
          query,
          numResults: numResults || 20,
          minPercentage,
          session,
          sinceDate: formattedSinceDate, // Send the formatted date to the backend
        })
      );
      setIsLoading(false);
    } catch (error) {
      console.error('Failed to fetch matches: ', error);
      setIsLoading(false);
    }
  }, [dispatch, query, numResults, minPercentage, session, sinceDate]);

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    performSearch();
  };

  const handleDateChange = (date) => {
    setSinceDate(date);
  };

  const handleClearDate = () => {
    setSinceDate(null);
  };

  return (
    <div className="min-h-[calc(100vh-75px)]">
      <div className="w-full space-y-4">
        {/* Stays pinned just below the fixed top bar while the results scroll. */}
        <StickyBar className="rounded-lg border border-border bg-card px-4 py-3.5 shadow-sm">
          <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2">
            <h2 className="text-xl font-semibold tracking-tight text-foreground">AI Search &amp; Analysis</h2>
            <p className="hidden text-sm text-muted-foreground sm:block">
              Semantic + keyword search across the bill corpus.
            </p>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <label className="inline-flex items-center gap-2 whitespace-nowrap text-sm text-muted-foreground">
                <input
                  type="checkbox"
                  checked={onlyStrongMatches}
                  onChange={handleCheckboxChange}
                  className="h-4 w-4 rounded border-border accent-primary"
                />
                Strong only
              </label>
              <Button onClick={handleSearch}>
                <IoSearch className="h-4 w-4" />
                Search
              </Button>
              <Button variant="outline" onClick={handlePrintResults}>
                <IoPrintOutline className="h-4 w-4" />
                Print
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-2 lg:flex-row lg:flex-wrap lg:items-center lg:gap-3">
            <input
              type="text"
              className="block h-9 min-w-0 rounded-md border border-input bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:min-w-[280px] lg:flex-1"
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for a bill…"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch();
              }}
            />

            <div className="flex flex-wrap items-center gap-2">
              <div className="w-32">
                <Dropdown
                  id="Numberofitem"
                  value={numResults}
                  options={['20', '30', '40', '50']}
                  title="# of Matches"
                  onChange={(e) => setNumResults(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <label htmlFor="since-date" className="whitespace-nowrap text-sm text-muted-foreground">
                  Since:
                </label>
                <DatePicker
                  id="since-date"
                  selected={sinceDate}
                  onChange={handleDateChange}
                  className="h-9 w-32 rounded-md border border-input bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  placeholderText="Select a date…"
                  dateFormat="MM/dd/yyyy"
                />
                {sinceDate && (
                  <button
                    type="button"
                    onClick={handleClearDate}
                    className="text-muted-foreground hover:text-foreground"
                    aria-label="Clear date"
                  >
                    <IoCloseCircle className="h-5 w-5" />
                  </button>
                )}
              </div>
              <div className="w-28">
                <Dropdown
                  id="session-id"
                  options={sessions}
                  title="Session"
                  value={session}
                  onChange={(e) => dispatch(setSession(e.target.value))}
                  disabled={sessions.length <= 1}
                />
              </div>
            </div>
          </div>
        </StickyBar>

        <div className="bg-slate-50 rounded-xl shadow-lg shadow-slate-300/50 border border-slate-200/60 overflow-hidden">
          <div className="p-4 sm:p-5">
            <div className="mb-4 flex items-baseline justify-between border-b border-slate-200 pb-3">
              <h2 className="text-lg font-semibold text-slate-800">Bill Results</h2>
              {results?.length > 0 && (
                <span className="text-xs tracking-wide text-slate-400">
                  {results.length} match{results.length === 1 ? '' : 'es'}
                </span>
              )}
            </div>

            <Table
              headers={['Bill Number', 'Track', 'Author', 'Title']}
              rows={results?.map((item) => [
                <button
                  type="button"
                  className="text-blue-600 hover:text-blue-700 hover:underline font-medium"
                  onClick={() => handleBillClick(item.id)}
                >
                  {item.id}
                </button>,
                tracks[item.id] && <img alt="tracked" src={tdot} className="h-4 w-4 opacity-80" />,
                item.metadata?.author,
                item.metadata?.title,
              ])}
              loading={isLoading}
            />

            {isShowingFluffedResults && (
              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                There were not enough matches that met your threshold, so we've shown the top 5
                results. They may not all be quality matches — refine your query for better
                accuracy.
              </div>
            )}

            <p className="mt-4 text-xs leading-5 text-slate-400">
              Responses are generated by AI and do not reflect the views of our company. Verify
              independently.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SearchPage;
